import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  MapPin, Clock, Shield, Users, Phone, ChevronRight, ChevronDown, CheckCircle2, Plane, MessageCircle
} from "lucide-react";
import PageTransition from "../components/PageTransition";
import PublicHeader from "../components/PublicHeader";
import SeoFooter from "../components/SeoFooter";
import { getLocation, locations } from "../data/locationsData";

const LocationPage = () => {
  const { slug } = useParams();
  const loc = getLocation(slug);
  const [openFaq, setOpenFaq] = React.useState(null);

  if (!loc) return <Navigate to="/" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        name: `Limo Near Me - ${loc.name}`,
        description: loc.metaDesc,
        address: { "@type": "PostalAddress", addressLocality: loc.name, addressRegion: loc.state, addressCountry: "AU" },
        telephone: "+61481437772",
        email: "info@limonearme.com.au",
        url: `https://www.limonearme.com.au/limo-hire/${loc.slug}`,
        priceRange: "$$-$$$",
        openingHours: "Mo-Su 00:00-23:59",
        areaServed: { "@type": "City", name: loc.name }
      },
      {
        "@type": "FAQPage",
        mainEntity: loc.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a }
        }))
      }
    ]
  };

  const highlights = [
    { icon: Shield, text: "Professional licensed chauffeurs" },
    { icon: Clock, text: "24/7 service, fixed upfront prices" },
    { icon: Plane, text: "Flight tracking & meet-and-greet" },
    { icon: Users, text: "Vehicles for 1 to 200+ guests" }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen bg-background" data-testid={`location-page-${loc.slug}`}>
        <Helmet>
          <title>{loc.metaTitle}</title>
          <meta name="description" content={loc.metaDesc} />
          <link rel="canonical" href={`https://www.limonearme.com.au/limo-hire/${loc.slug}`} />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>

        <PublicHeader />

        {/* Hero */}
        <section className="pt-28 pb-16 bg-gradient-to-b from-amber-50 via-white to-white">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 mb-6">
                <MapPin className="h-4 w-4" />
                <span className="text-sm font-medium">{loc.name}, {loc.state} · {loc.region}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-4">{loc.h1}</h1>
              <p className="text-lg text-muted-foreground mb-3">{loc.tagline}</p>
              <p className="text-sm font-medium text-amber-700 mb-8 flex items-center justify-center gap-2">
                <Plane className="h-4 w-4" /> {loc.driveTime}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/book">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg font-bold" data-testid="location-book-btn">
                    Get an Instant Quote <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/61481437772" target="_blank" rel="noreferrer">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg bg-green-600 hover:bg-green-700 text-white">
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp Us
                  </Button>
                </a>
                <a href="tel:+61481437772">
                  <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg">
                    <Phone className="mr-2 h-5 w-5" /> Call Now
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Intro copy + highlights */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-5">
              {loc.intro.map((p, i) => (
                <p key={i} className="text-muted-foreground text-lg leading-relaxed">{p}</p>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 bg-amber-50 rounded-xl p-4">
                    <h.icon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground">{h.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-950 text-white rounded-2xl p-8 h-fit">
              <h2 className="text-lg font-bold mb-4 text-amber-400">Popular {loc.name} Routes</h2>
              <div className="space-y-4">
                {loc.routes.map((r, i) => (
                  <div key={i} className="pb-4 border-b border-white/10 last:border-0 last:pb-0">
                    <p className="text-sm text-gray-300">{r.from}</p>
                    <p className="text-sm text-gray-300 mb-1">→ {r.to}</p>
                    <Link to="/book" className="text-amber-400 text-sm font-semibold hover:underline">Get instant price →</Link>
                  </div>
                ))}
              </div>
              <Link to="/book">
                <Button className="w-full mt-6 rounded-full" data-testid="location-routes-book-btn">Book a Transfer</Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What we offer */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Chauffeur Services in {loc.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Private Airport Transfers", desc: `Door-to-door transfers between ${loc.name} and ${loc.airport}, with flight tracking and meet-and-greet included.` },
                { title: "Minibus & Group Charter", desc: `Mercedes Sprinter minibuses seating up to 15 — ideal for ${loc.name} groups travelling to airports, cruises, weddings and events together.` },
                { title: "Corporate Travel", desc: `Quiet, punctual executive cars for ${loc.name} businesses, with corporate accounts and monthly invoicing available.` },
                { title: "Weddings & Occasions", desc: `Elegant, on-time transport for weddings, formals and celebrations across ${loc.region}.` }
              ].map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white rounded-xl p-6 border border-black/5 hover:shadow-lg hover:border-amber-300 transition-all">
                  <CheckCircle2 className="h-6 w-6 text-amber-600 mb-3" />
                  <h3 className="font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{loc.name} Transfer FAQs</h2>
            <div className="space-y-4">
              {loc.faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-black/10 p-5">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between text-left" data-testid={`location-faq-${index}`}>
                    <h3 className="font-semibold pr-4">{faq.q}</h3>
                    <ChevronDown className={`h-5 w-5 text-amber-600 transition-transform flex-shrink-0 ${openFaq === index ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === index && <p className="text-muted-foreground text-sm mt-4 pt-4 border-t border-black/10">{faq.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby areas */}
        <section className="py-14 bg-amber-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-xl font-bold mb-6">We Also Service Areas Near {loc.name}</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {loc.nearby.map((n) => {
                const nl = locations.find((l) => l.slug === n);
                return nl ? (
                  <Link key={n} to={`/limo-hire/${n}`} className="px-5 py-2 rounded-full bg-white border border-amber-200 text-sm font-medium hover:bg-amber-600 hover:text-white transition-colors">
                    {nl.name}
                  </Link>
                ) : null;
              })}
              <Link to="/chauffeur/brisbane" className="px-5 py-2 rounded-full bg-white border border-amber-200 text-sm font-medium hover:bg-amber-600 hover:text-white transition-colors">Brisbane</Link>
              <Link to="/chauffeur/gold-coast" className="px-5 py-2 rounded-full bg-white border border-amber-200 text-sm font-medium hover:bg-amber-600 hover:text-white transition-colors">Gold Coast</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950 text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4">Ready to Book Your {loc.name} Transfer?</h2>
            <p className="text-gray-400 mb-8">Instant online quotes, fixed prices and professional chauffeurs — available 24/7.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book"><Button size="lg" className="rounded-full px-10 py-6 text-lg font-bold" data-testid="location-cta-book-btn">Book Online Now</Button></Link>
              <a href="tel:+61481437772"><Button size="lg" variant="outline" className="rounded-full px-10 py-6 text-lg border-white/30 text-white hover:bg-white/10"><Phone className="mr-2 h-5 w-5" /> +61 481 437 772</Button></a>
            </div>
          </div>
        </section>

        <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default LocationPage;
